import * as React from 'react';
import InfiniteLoader from 'react-window-infinite-loader';
import { FixedSizeList } from 'react-window';
import { RawChartSchema, SortType } from 'common/types/chart';
import { TableListContainer, ListItemSpinner, FolderWrapper } from './styled';
import { ITEM_TYPES, SortingCriteria, SortingKey, TableListItemProps, TableRowItemProps } from 'common/types/common';
import Spinner from 'common/components/Spinner';
import { FetchNextPage, ListItemType, TableListHeaderProps } from 'common/types/charts';
import { useGetViewportSize } from 'common/hooks/useGetViewportSize';
import { ActionItems, DataTestId } from 'settings/constants';
import { useChartDashboardsContext } from 'common/contexts/ChartsContext';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Folder } from 'common/types/folders';
import { StyledItemContainer } from './styled';
import FolderItem from './FolderItem';
import { Fallback } from 'router';
import { useTableDragAndDrop } from 'common/components/TableList/hooks/useTableDragAndDrop';
import { useFolderManager } from './hooks/useFolderManager';
import { getFolderType, getItemDataProperty, getUrlPrefix } from './utils';
import { DashboardDetails } from 'common/types/dashboard';

const FolderSelectionModal = React.lazy(() => import('../FolderSelectionModal'));

type RegularListItem = { type: ITEM_TYPES.ITEM; data: TableListItemProps };
type FolderListItem = { type: ITEM_TYPES.FOLDER; data: FolderListItemProps };
type FolderListItemProps = { id: string; name: string; count: number; folder_type: string };
type CombinedItem = FolderListItem | RegularListItem;

interface TableListProps {
  type: ListItemType;
  items: Array<TableListItemProps>;
  folders: Array<Folder>;
  subFolders: DashboardDetails[] | RawChartSchema[];
  renderListItem: (listItemProps: TableRowItemProps) => JSX.Element;
  onHandleListSort?: ({ key, type }: SortingCriteria) => void;
  isDataLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  viewportOffset?: number;
  fetchNextpage?: FetchNextPage;
  onItemDuplicate?: (item: TableListItemProps) => void;
  onItemDelete?: (item: TableListItemProps) => void;
  renderHeader?: (headerProps: TableListHeaderProps) => unknown;
  onUpdateItemFolder?: (itemId: string, folderId: string) => Promise<void>;
  onRenameFolder?: (oldName: string, newName: string) => Promise<void>;
  onRemoveItemFromFolder?: (itemId: string, folderId: string) => Promise<void>;
  onDeleteFolder?: (folderId: string) => Promise<void>;
  folderPath?: Array<{ id: string; name: string }>;
  currentFolderId?: string | null;
  onFolderPathChange?: (path: Array<{ id: string; name: string }>, folderId: string | null) => void;
  onNavigateToFolder?: (folderId: string, folderName: string) => void;
  isSubFoldersLoading?: boolean;
  refetchSubFolders?: () => void;
}

const ROW_HEIGHT = 49;

const EmptyFolderMessage: React.FC = () => (
  <div style={{ padding: '20px', textAlign: 'center' }}>
    <p>This folder is empty</p>
  </div>
);

const TableSpinner: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <ListItemSpinner style={style}>
    <Spinner size='extraSmall' />
  </ListItemSpinner>
);

const TableList = ({
  type,
  items = [],
  folders = [],
  onItemDuplicate,
  isDataLoading = false,
  onItemDelete,
  onHandleListSort,
  viewportOffset = 186,
  hasNextPage,
  isFetchingNextPage,
  fetchNextpage,
  renderHeader,
  renderListItem,
  onUpdateItemFolder,
  onRenameFolder,
  onRemoveItemFromFolder,
  onDeleteFolder,
  onFolderPathChange,
  folderPath,
  currentFolderId,
  onNavigateToFolder,
  subFolders = [],
  isSubFoldersLoading = false,
  refetchSubFolders,
}: TableListProps) => {
  const navigate = useNavigate();

  const containerRef = React.useRef<HTMLDivElement>(null);
  const unfolderedAreaRef = useRef<HTMLDivElement>(null);

  const { viewportHeight } = useGetViewportSize({ heightOffset: viewportOffset });
  const { setModalOpen, setSelectedChart } = useChartDashboardsContext();

  const [showFolderSelectionModal, setShowFolderSelectionModal] = useState(false);
  const [itemIdToMove, setItemIdToMove] = useState<string | null>(null);

  const [sortingCriteria, setSortingCriteria] = React.useState<SortingCriteria>({
    key: SortingKey.UPDATED_AT,
    type: SortType.DESC,
  });

  const isFolderContentLoading = Boolean(currentFolderId) && isSubFoldersLoading;

  const itemDataProperty = getItemDataProperty(type);

  const { setupFolderDropTarget, setupDraggable } = useTableDragAndDrop({
    onUpdateItemFolder,
    onRemoveItemFromFolder,
    items,
    itemDataKey: itemDataProperty,
    unfolderedAreaRef,
  });

  const { enrichedFolders, unfolderedItems, setOpenFolders, getItemsForFolder } = useFolderManager(
    folders,
    items,
    type,
    subFolders,
    currentFolderId,
  );

  const navigateToFolder = useCallback(
    (folderId: string, folderName: string) => {
      onNavigateToFolder?.(folderId, folderName);
    },
    [onNavigateToFolder],
  );

  const onHandleClick = React.useCallback(
    (itemId: string) => {
      navigate(`/${getUrlPrefix(type)}/${itemId}`);
    },
    [type, navigate],
  );

  const onHandleSort = React.useCallback(
    (columnName: SortingKey) => {
      if (!columnName) return;

      let sortingType;

      switch (sortingCriteria?.type) {
        case SortType.ASC:
          sortingType = SortType.DEFAULT;
          break;
        case SortType.DESC:
          sortingType = SortType.ASC;
          break;
        default:
          sortingType = SortType.DESC;
      }

      const newCriteria = { key: columnName, type: sortingType };

      setSortingCriteria(newCriteria);
      onHandleListSort && onHandleListSort(newCriteria);
    },
    [setSortingCriteria, sortingCriteria, onHandleListSort],
  );

  const handleActionSelect = React.useCallback(
    (actions: string[], item: TableListItemProps) => {
      switch (actions[0]) {
        case ActionItems.OPEN:
          onHandleClick(item.uuid);
          break;
        case ActionItems.DUPLICATE:
          onItemDuplicate && onItemDuplicate(item);
          break;
        case ActionItems.DELETE:
          onItemDelete && onItemDelete(item);
          break;
        case ActionItems.ADD_TO_DASHBOARD:
          setModalOpen(true);
          setSelectedChart(item.uuid, item.chart_type || '');
          break;
        case ActionItems.MOVE_TO_FOLDER:
          setItemIdToMove(item.uuid);
          setShowFolderSelectionModal(true);
          break;
        case ActionItems.REMOVE_FROM_FOLDER:
          if (currentFolderId && onRemoveItemFromFolder) {
            onRemoveItemFromFolder(item.uuid, currentFolderId).then(() => {
              refetchSubFolders?.();
            });
          }
          break;
        default:
          break;
      }
    },
    [
      onHandleClick,
      onItemDuplicate,
      onItemDelete,
      setModalOpen,
      setSelectedChart,
      setItemIdToMove,
      setShowFolderSelectionModal,
      currentFolderId,
      onRemoveItemFromFolder,
      refetchSubFolders,
    ],
  );

  const handleRenameFolder = useCallback(
    async (folderId: string, newName: string) => {
      if (onRenameFolder) {
        await onRenameFolder(folderId, newName);
        // Update openFolders state with new name if needed
        setOpenFolders(prev => {
          const newState = { ...prev };
          if (newState[folderId] !== undefined) {
            newState[newName] = newState[folderId];
            delete newState[folderId];
          }
          return newState;
        });
      }
    },
    [onRenameFolder, setOpenFolders],
  );

  const handleDeleteFolder = useCallback(
    (folderId: string) => {
      if (onDeleteFolder) {
        onDeleteFolder(folderId);
      }
    },
    [onDeleteFolder],
  );

  useEffect(() => {
    if (onFolderPathChange && folderPath) {
      onFolderPathChange(folderPath, currentFolderId ?? null);
    }
  }, [folderPath, currentFolderId, onFolderPathChange]);

  const combinedItems = useMemo(() => {
    if (currentFolderId === null) {
      // At root level, combine folders and unfoldered items
      const folderItems = enrichedFolders.map(folder => ({
        type: ITEM_TYPES.FOLDER,
        data: folder,
      })) as FolderListItem[];

      const normalItems = unfolderedItems.map(item => ({
        type: ITEM_TYPES.ITEM,
        data: item,
      })) as RegularListItem[];

      return [...folderItems, ...normalItems];
    } else {
      // In a folder, just show items in that folder
      return getItemsForFolder(currentFolderId ?? '').map(item => ({
        type: ITEM_TYPES.ITEM,
        data: item,
      })) as CombinedItem[];
    }
  }, [currentFolderId, enrichedFolders, unfolderedItems, getItemsForFolder]);

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = (index: number) => !hasNextPage || index < combinedItems.length;
  // If there are more items to be loaded then add an extra row to hold a loading indicator.
  const itemCount = hasNextPage ? combinedItems.length + 1 : combinedItems.length;
  // Only load 1 page of items at a time.
  const loadMoreItems = (isFetchingNextPage ? () => {} : fetchNextpage) as unknown as (_1: number, _2: number) => void | Promise<void>;

  const renderFolder = (folder: FolderListItemProps, style: React.CSSProperties): JSX.Element => {
    return (
      <FolderWrapper
        id={`folder-${folder.id}`}
        style={style}
        ref={(el: HTMLElement | null) => {
          setupFolderDropTarget(folder.id, el);
        }}
      >
        <FolderItem
          name={folder.name}
          count={folder.count}
          data-testid={DataTestId.DASHBOARD_FOLDER_ITEM}
          onDoubleClick={() => navigateToFolder(folder.id, folder.name)}
          onRename={onRenameFolder ? newName => handleRenameFolder(folder.id, newName) : undefined}
          onDelete={onDeleteFolder ? () => handleDeleteFolder(folder.id) : undefined}
        />
      </FolderWrapper>
    );
  };

  const renderRegularItem = (item: TableListItemProps, style: React.CSSProperties): JSX.Element => {
    return (
      <StyledItemContainer
        style={style}
        ref={el => {
          if (el) setupDraggable(item.uuid, el);
        }}
        className='item-container'
      >
        {renderListItem({
          listItemData: item,
          style: {},
          onClick: onHandleClick,
          onSelect: handleActionSelect,
        })}
      </StyledItemContainer>
    );
  };

  const renderListRootLevel = (): JSX.Element => {
    return (
      <InfiniteLoader isItemLoaded={isItemLoaded} itemCount={itemCount} loadMoreItems={loadMoreItems}>
        {({ onItemsRendered, ref }) => (
          <FixedSizeList
            height={Math.min(ROW_HEIGHT * Math.max(itemCount, 5), viewportHeight)}
            width='100%'
            itemCount={itemCount}
            itemSize={ROW_HEIGHT}
            onItemsRendered={onItemsRendered}
            ref={ref}
          >
            {({ index, style }) => {
              if (!isItemLoaded(index)) {
                return <TableSpinner style={style} />;
              }

              const item = combinedItems[index];

              if (item.type === ITEM_TYPES.FOLDER) {
                return renderFolder(item.data, style);
              } else {
                return renderRegularItem(item.data, style);
              }
            }}
          </FixedSizeList>
        )}
      </InfiniteLoader>
    );
  };

  const renderFolderContent = (currentFolderId = ''): JSX.Element => {
    const items = getItemsForFolder(currentFolderId);
    const isEmpty = items.length === 0;

    const renderItems = () =>
      items.map(item => {
        return (
          <StyledItemContainer
            key={item.uuid}
            className='item-container'
            style={{ height: ROW_HEIGHT }}
            ref={el => el && item.uuid && setupDraggable(item.uuid, el)}
          >
            {renderListItem({
              listItemData: {
                ...(item as TableListItemProps),
                currentFolderId,
              },
              style: {},
              onClick: onHandleClick,
              onSelect: handleActionSelect,
            })}
          </StyledItemContainer>
        );
      });

    return (
      <div style={{ maxHeight: viewportHeight, overflowY: 'auto' }} ref={unfolderedAreaRef}>
        {isFolderContentLoading && <TableSpinner />}

        {!isFolderContentLoading && isEmpty && <EmptyFolderMessage />}

        {!isFolderContentLoading && !isEmpty && renderItems()}
      </div>
    );
  };

  return (
    <TableListContainer ref={containerRef}>
      {renderHeader &&
        renderHeader({
          onColumnSort: onHandleSort,
          sortingKey: sortingCriteria.key,
          sortingType: sortingCriteria.type,
        })}

      {isDataLoading ? (
        <TableSpinner />
      ) : (
        <div id='scrollable-content'>{currentFolderId === null ? renderListRootLevel() : renderFolderContent(currentFolderId)}</div>
      )}

      <React.Suspense fallback={<Fallback />}>
        <FolderSelectionModal
          title='Move to Folder'
          isOpen={showFolderSelectionModal}
          onClose={() => setShowFolderSelectionModal(false)}
          onConfirm={folderId => {
            if (itemIdToMove && onUpdateItemFolder) {
              onUpdateItemFolder(itemIdToMove, folderId);
              setShowFolderSelectionModal(false);
              setItemIdToMove(null);
            }
          }}
          folders={(folders || []).filter(folder => folder.folder_type === getFolderType(type))}
          itemName={items.find(item => item.uuid === itemIdToMove)?.name || ''}
        />
      </React.Suspense>
    </TableListContainer>
  );
};

export default TableList;
